import type { RowLayoutInput } from './wholeCardRow';

export type RowElements = {
  /** The box that holds the scrolling frame and, on desktop, the pinned new-one card. */
  row: HTMLElement;
  /** The box that scrolls sideways. Its children are the identity cards. */
  scroller: HTMLElement;
  /** An unseen box as wide as the theme's card, to read the narrowest a card may be from. */
  probe: HTMLElement;
  /**
   * An unseen box as wide as the room the row keeps clear for the right arrow beside the pinned
   * new-one card, or null where nothing is pinned.
   */
  clearance: HTMLElement | null;
  /** The new-one card sits in the row and takes a slot (desktop), rather than under it (phone). */
  pinned: boolean;
};

/**
 * Reads the widths the layout depends on from the browser: the room the cards may fill, the
 * narrowest a card may be, and the gap between two. The number of cards is only handed on, to say
 * whether they fit; it never sets a width. Returns null when there are no identity cards, or the
 * row has no layout.
 *
 * The room is the row's width less the scroller's edge padding on both sides, and less the room
 * kept clear for the arrow beside the pinned new-one card. The new-one card, on desktop, is inside
 * it.
 */
export function readRowMeasurements({
  row,
  scroller,
  probe,
  clearance,
  pinned,
}: Readonly<RowElements>): RowLayoutInput | null {
  const count = scroller.children.length;
  const minCardWidth = probe.getBoundingClientRect().width;
  // A row the page has not laid out (or has hidden) has no width to share out. Applying a layout
  // made of zeros would give cards of no width, and a measure that never settles.
  if (count === 0 || minCardWidth <= 0) {
    return null;
  }
  const style = window.getComputedStyle(scroller);
  const edgePadding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
  const clearanceWidth = pinned ? (clearance?.getBoundingClientRect().width ?? 0) : 0;
  return {
    count,
    pinned,
    minCardWidth,
    gap: parseFloat(style.columnGap) || 0,
    available: row.getBoundingClientRect().width - edgePadding - clearanceWidth,
  };
}
